/* eslint-disable max-lines-per-function */
import { useContext, useEffect, useMemo, useState } from 'react';
import { Backsplash, BlueprintInstanceType, COUNTERTOP_TEXTURE_DIMENSION } from '@rta/rta-blueprint-library';

import { AppContext } from '@r-app/app-context';
import { type CleanEdgeLabel, isCleanEdgeCountertop } from '../blueprint-view/helpers/generate-clean-edge-annotations';
import { DisplayMode } from '@r-core';
import { RenderCleanEdgeLabel } from '../blueprint-view/shared/render-clean-edge-label';
import { renderTracking } from '@r-services/render-tracking';
import {
	renderPanelRect,
	renderPanelDimensions,
	renderPanelBreakpoints,
	createTexturePattern
} from './render-panel-base';

const BACKSPLASH_SPACING = 4;
const BACKSPLASH_HORIZONTAL_SPACING = 2;
const CLEAN_EDGE_TEXT = 'CLEAN EDGE';
// Same font size the countertop clean-edge label uses (embedded-svg-viewport / render-joint-countertop-row pass 2.2) so the two read identically.
const CLEAN_EDGE_FONT_SIZE = 2.2;
const CLEAN_EDGE_SEAM_ROTATION = -90;
// Bottom cut-edge label sits just OUTSIDE the strip's bottom edge (matches the countertop end-label convention), ~one line-height clear of the fill.
const CLEAN_EDGE_BOTTOM_OFFSET = CLEAN_EDGE_FONT_SIZE;
// Adjoining cut-edge label sits just OUTSIDE the shortened side edge (matches the countertop side end-label convention: vertical text beyond the edge).
const CLEAN_EDGE_SIDE_OFFSET = 2;

type BacksplashInfo = {
	id: string;
	backsplash: Backsplash;
	hasBreakpoints: boolean;
};

type BacksplashGroup = {
	key: string;
	width: number;
	height: number;
	count: number;
	countertopId: string;
	materialTexture: string;
	materialScale: { width: number; height: number };
	/** Chiseled-edge concrete slabs get "CLEAN EDGE" labels on every cut edge; each backsplash inherits its parent countertop's material. */
	isCleanEdge: boolean;
	backsplashes: BacksplashInfo[];
};

/**
 * "CLEAN EDGE" label positions for a chiseled-edge concrete backsplash strip — one down each
 * breakpoint (seam), one along the bottom cut edge, and one down any side shortened at a corner
 * (adjoining depth > 0 = a cut edge butting the neighbouring backsplash) — in the strip group's
 * (untranslated) frame. Rendered through the shared RenderCleanEdgeLabel so it matches the
 * countertop clean-edge note exactly: same font, colour, and draggable/deletable/persisted behaviour.
 */
const getBacksplashCleanEdgeLabels = (params: {
	backsplashId: string;
	breakpoints: Array<{ id: string; center: number }>;
	width: number;
	height: number;
	leftAdjoiningDepth: number;
	rightAdjoiningDepth: number;
	centerX: number;
	centerY: number;
}): Array<CleanEdgeLabel> => {
	const { backsplashId, breakpoints, width, height, leftAdjoiningDepth, rightAdjoiningDepth, centerX, centerY } = params;

	const labels: Array<CleanEdgeLabel> = breakpoints.map((bp) => ({
		id: `backsplash.${backsplashId}.seam.${bp.id}`,
		x: centerX + bp.center,
		y: centerY,
		rotation: CLEAN_EDGE_SEAM_ROTATION,
		text: CLEAN_EDGE_TEXT
	}));

	labels.push({
		id: `backsplash.${backsplashId}.bottom`,
		x: centerX,
		y: centerY + (height / 2) + CLEAN_EDGE_BOTTOM_OFFSET,
		rotation: 0,
		text: CLEAN_EDGE_TEXT
	});

	if (leftAdjoiningDepth > 0) {
		labels.push({
			id: `backsplash.${backsplashId}.left`,
			x: centerX - (width / 2) - CLEAN_EDGE_SIDE_OFFSET,
			y: centerY,
			rotation: CLEAN_EDGE_SEAM_ROTATION,
			text: CLEAN_EDGE_TEXT
		});
	}

	if (rightAdjoiningDepth > 0) {
		labels.push({
			id: `backsplash.${backsplashId}.right`,
			x: centerX + (width / 2) + CLEAN_EDGE_SIDE_OFFSET,
			y: centerY,
			rotation: CLEAN_EDGE_SEAM_ROTATION,
			text: CLEAN_EDGE_TEXT
		});
	}

	return labels;
};

export const RenderBacksplashes = (): React.ReactElement => {
	const { controller: { options, kitchen, navigation }} = useContext(AppContext);

	const [unstackedGroups, setUnstackedGroups] = useState<Set<string>>(new Set());

	const [breakpointVersion, setBreakpointVersion] = useState(0);

	useEffect(() => {
		const subscriptionId = kitchen.subscribe('breakpoint', '*', () => {
			setBreakpointVersion((v) => v + 1);
		});

		return () => {
			kitchen.unsubscribe(subscriptionId);
		};
	}, [kitchen]);

	const backsplashGroups = useMemo<BacksplashGroup[]>(() => {
		const groups: Map<string, BacksplashGroup> = new Map();
		const processedPairedIds: Set<string> = new Set();

		// Build a map of all backsplashes across all countertops for cross-island pairing lookup
		const allBacksplashesMap = new Map<string, { backsplash: Backsplash; countertop: typeof kitchen.design.islands.children[0] extends { getCountertop: () => infer C } ? C : never }>();
		kitchen.design.islands.children.forEach((island) => {
			const countertop = island.getCountertop();
			const backsplashes = countertop?.backsplashes?.children ?? [];
			backsplashes.forEach((panel) => {
				const backsplash = panel as Backsplash;
				allBacksplashesMap.set(backsplash.id, { backsplash, countertop });
			});
		});

		kitchen.design.islands.children.forEach((island) => {
			const countertop = island.getCountertop();
			const backsplashes = countertop?.backsplashes?.children ?? [];
			const materialAsset = countertop?.material;

			backsplashes.forEach((panel) => {
				const backsplash = panel as Backsplash;

				// Skip if this backsplash was already processed as part of a pair
				if (processedPairedIds.has(backsplash.id)) return;

				let width = Math.round((backsplash.size?.width ?? 0) * 100) / 100;
				const height = Math.round((backsplash.size?.height ?? 4) * 100) / 100;
				const hasBreakpoints = backsplash.breakpoints?.children?.length > 0;

				// Check if this backsplash is merged/paired with another
				// Search across ALL countertops to support cross-island merging
				const pairedId = backsplash.pairedBacksplashId;
				let isMerged = false;
				if (pairedId) {
					const pairedEntry = allBacksplashesMap.get(pairedId);
					if (pairedEntry) {
						const pairedBacksplash = pairedEntry.backsplash;
						// Combine widths for merged backsplashes
						const pairedWidth = Math.round((pairedBacksplash.size?.width ?? 0) * 100) / 100;
						width += pairedWidth;
						isMerged = true;
						// Mark the paired backsplash as processed so it won't be rendered separately
						processedPairedIds.add(pairedId);
					}
				}

				// Use merged key for paired backsplashes to prevent stacking them with non-merged ones
				const key = hasBreakpoints ? `bp-${backsplash.id}` : (isMerged ? `merged-${backsplash.id}` : `${width}-${height}`);

				const backsplashInfo: BacksplashInfo = {
					id: backsplash.id,
					backsplash,
					hasBreakpoints
				};

				if (groups.has(key) && !hasBreakpoints && !isMerged) {
					const group = groups.get(key)!;
					group.count++;
					group.backsplashes.push(backsplashInfo);
				} else {
					groups.set(key, {
						key,
						width,
						height,
						count: 1,
						countertopId: countertop.id,
						materialTexture: materialAsset?.texture ?? '',
						materialScale: {
							width: COUNTERTOP_TEXTURE_DIMENSION * (materialAsset?.scale?.width || 1),
							height: COUNTERTOP_TEXTURE_DIMENSION * (materialAsset?.scale?.height || 1)
						},
						isCleanEdge: isCleanEdgeCountertop(countertop),
						backsplashes: [backsplashInfo]
					});
				}
			});
		});

		return Array.from(groups.values()).sort((a, b) => b.width - a.width);
	}, [kitchen, breakpointVersion]);

	// repair persisted out-of-range centers so breakpoints can never render off the strip
	useEffect(() => {
		backsplashGroups.forEach(({ width, backsplashes }) => {
			const limit = (width / 2) - 0.0625;
			backsplashes.forEach(({ backsplash }) => {
				(backsplash.breakpoints?.children ?? []).forEach((breakpoint) => {
					if (Math.abs(breakpoint.center) > limit) {
						breakpoint.center = Math.max(-limit, Math.min(limit, breakpoint.center));
					}
				});
			});
		});
	}, [backsplashGroups]);

	useMemo(() => {
		type BacksplashViewState = {
			unstackedGroups: Set<string>;
			unstackGroup: (groupKey: string) => void;
			stackGroup: (groupKey: string) => void;
			canStackGroup: (groupKey: string) => boolean;
			getGroupKeyForBacksplash: (backsplashId: string) => string | null;
			isGroupUnstacked: (groupKey: string) => boolean;
		};

		(kitchen as typeof kitchen & { __backsplashViewState: BacksplashViewState }).__backsplashViewState = {
			unstackedGroups,
			unstackGroup: (groupKey: string): void => {
				setUnstackedGroups((prev) => new Set([...prev, groupKey]));
			},
			stackGroup: (groupKey: string): void => {
				setUnstackedGroups((prev) => {
					const next = new Set(prev);
					next.delete(groupKey);
					return next;
				});
			},
			canStackGroup: (groupKey: string): boolean => {
				const group = backsplashGroups.find((g) => g.key === groupKey);
				if (!group) return false;
				return !group.backsplashes.some((bs) => bs.hasBreakpoints);
			},
			getGroupKeyForBacksplash: (backsplashId: string): string | null => {
				for (const group of backsplashGroups) {
					if (group.backsplashes.some((bs) => bs.id === backsplashId)) {
						return group.key;
					}
				}
				return null;
			},
			isGroupUnstacked: (groupKey: string): boolean => {
				return unstackedGroups.has(groupKey);
			}
		};
	}, [unstackedGroups, backsplashGroups, kitchen]);

	renderTracking.incrementRender('RenderBacksplashes');

	// Check if backsplashes should be shown (default to true for backward compatibility)
	const showBacksplashes = options.showBacksplashes ?? true;

	if (backsplashGroups.length === 0 || !showBacksplashes) {
		return <></>;
	}

	let currentY = 0;
	const groupPositions = backsplashGroups.map((group) => {
		const isUnstacked = unstackedGroups.has(group.key);
		const effectiveHeight = isUnstacked ? group.height : group.height;
		const y = currentY + (effectiveHeight / 2);
		currentY += effectiveHeight + BACKSPLASH_SPACING;
		return { ...group, centerY: y, isUnstacked };
	});

	const isManufacturingMode = navigation.displayMode === DisplayMode.Manufacturing ||
		navigation.displayMode === DisplayMode.BacksplashManufacturing;
	const showTexture = !isManufacturingMode;
	const includeLabelBox = !isManufacturingMode;

	const uniqueCountertops = [...new Set(backsplashGroups.map((d) => d.countertopId))];

	const fontSize = 2 * options.dimensionFontSizeFactor;

	const renderSingleBacksplash = (
		backsplashInfo: BacksplashInfo,
		centerX: number,
		centerY: number,
		width: number,
		height: number,
		countertopId: string,
		index: number
	) => {
		const { backsplash } = backsplashInfo;
		const breakpoints = backsplash.breakpoints?.children ?? [];

		return (
			<g key={`backsplash-${backsplashInfo.id}`} id={`backsplash-${backsplashInfo.id}`} transform={`translate(${centerX}, ${centerY})`}>
				{/* Draw backsplash rectangle with texture or solid fill - using shared utility */}
				{renderPanelRect({
					width,
					height,
					center: { x: 0, y: 0 },
					contextCenter: { x: centerX, y: centerY },
					instanceType: BlueprintInstanceType.Backsplash,
					id: backsplashInfo.id,
					fill: showTexture ? `url(#backsplash-texture-${countertopId})` : 'darkgray',
					stroke: 'none'
				})}

				{/* Render breakpoints - using shared utility */}
				{renderPanelBreakpoints({
					breakpoints,
					height,
					showBreakpoints: options.showBreakpoints
				})}

				{/* Render dimensions - using shared utility */}
				{options.showPanelOutsideDimensions && renderPanelDimensions({
					width,
					height,
					center: { x: 0, y: 0 },
					includeLabelBox,
					breakpoints,
					showBreakpoints: options.showBreakpoints,
					paddingTop: -1.5,
					paddingSide: 0,
					isFirst: index === 0
				})}
			</g>
		);
	};

	return (
		<g id="backsplashes-view">
			{/* Create texture patterns for each countertop material - using shared utility */}
			{showTexture && (
				<defs>
					{uniqueCountertops
						.map((countertopId) => backsplashGroups.find((d) => d.countertopId === countertopId))
						.filter((group): group is BacksplashGroup => group !== undefined)
						.map((group) => createTexturePattern({
								key: `backsplash-texture-${group.countertopId}`,
								patternId: `backsplash-texture-${group.countertopId}`,
								textureUrl: `${options.assetsBaseUrl}/textures/${group.materialTexture}${options.assetsSasToken}`,
								width: group.materialScale.width,
								height: group.materialScale.height
							})
						)}
				</defs>
			)}

			{/* Render each grouped backsplash */}
			{groupPositions.map((group, groupIndex) => {
				const { width, height, count, countertopId, centerY, isUnstacked, backsplashes, isCleanEdge } = group;

				if (isUnstacked || count === 1) {
					const totalWidth = (width * count) + (BACKSPLASH_HORIZONTAL_SPACING * (count - 1));
					const startX = -totalWidth / 2 + width / 2;

					return (
						<g key={`backsplash-group-${groupIndex}`} id={`backsplash-group-${groupIndex}${isUnstacked ? '-unstacked' : ''}`}>
							{backsplashes.map((bsInfo, idx) => {
								const centerX = startX + (idx * (width + BACKSPLASH_HORIZONTAL_SPACING));
								return renderSingleBacksplash(bsInfo, centerX, centerY, width, height, countertopId, idx);
							})}
							{isCleanEdge && backsplashes.flatMap((bsInfo, idx) => {
								const centerX = startX + (idx * (width + BACKSPLASH_HORIZONTAL_SPACING));
								return getBacksplashCleanEdgeLabels({
									backsplashId: bsInfo.id,
									breakpoints: (bsInfo.backsplash.breakpoints?.children ?? []).map((bp) => ({ id: bp.id, center: bp.center })),
									width,
									height,
									leftAdjoiningDepth: bsInfo.backsplash.leftAdjoiningDepth ?? 0,
									rightAdjoiningDepth: bsInfo.backsplash.rightAdjoiningDepth ?? 0,
									centerX,
									centerY
								}).map((label) => <RenderCleanEdgeLabel key={label.id} label={label} fontSize={CLEAN_EDGE_FONT_SIZE} />);
							})}
						</g>
					);
				}

				const [firstBacksplash] = backsplashes;

				return (
					<g key={`backsplash-group-${groupIndex}`} id={`backsplash-group-${groupIndex}`}>
						{/* Draw backsplash rectangle with texture or solid fill - using shared utility */}
						{renderPanelRect({
							width,
							height,
							center: { x: 0, y: centerY },
							instanceType: BlueprintInstanceType.Backsplash,
							id: firstBacksplash.id,
							fill: showTexture ? `url(#backsplash-texture-${countertopId})` : 'darkgray',
							stroke: 'none'
						})}

						{isCleanEdge && getBacksplashCleanEdgeLabels({
							backsplashId: firstBacksplash.id,
							breakpoints: [],
							width,
							height,
							leftAdjoiningDepth: firstBacksplash.backsplash.leftAdjoiningDepth ?? 0,
							rightAdjoiningDepth: firstBacksplash.backsplash.rightAdjoiningDepth ?? 0,
							centerX: 0,
							centerY
						}).map((label) => <RenderCleanEdgeLabel key={label.id} label={label} fontSize={CLEAN_EDGE_FONT_SIZE} />)}

						{/* Count indicator (x2, x3, etc.) - shown on right side of panel */}
						<g>
							<rect
								x={(width / 2) + 1}
								y={centerY - (fontSize / 2) - 0.5}
								width={fontSize * 1.5}
								height={fontSize + 1}
								fill="white"
								stroke="black"
								strokeWidth="0.1"
								rx="0.5"
								ry="0.5"
							/>
							<text
								x={(width / 2) + 1 + (fontSize * 0.75)}
								y={centerY + (fontSize / 3)}
								textAnchor="middle"
								fill="red"
								fontSize={fontSize}
								className="rta-designer-component-measure rta-noselect"
							>
								x{count}
							</text>
						</g>

						{/* Render dimensions - using shared utility */}
						{options.showPanelOutsideDimensions && renderPanelDimensions({
							width,
							height,
							center: { x: 0, y: centerY },
							includeLabelBox,
							breakpoints: [],
							showBreakpoints: false,
							paddingTop: -1.5,
							paddingSide: 0,
							isFirst: true
						})}
					</g>
				);
			})}
		</g>
	);
};
